import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useI18n } from '@/providers/i18n';

import styles from './ContactForm.module.css';

const SUBJECTS = ['BOOKING', 'PRESS', 'PARTNERSHIP', 'GENERAL', 'LOST & FOUND'] as const;

const schema = z.object({
  subject: z.enum(SUBJECTS),
  name: z.string().trim().min(1, 'REQUIRED'),
  email: z.string().trim().email('INVALID EMAIL'),
  message: z.string().trim().min(10, 'TOO SHORT'),
});

type ContactValues = z.infer<typeof schema>;

export default function ContactForm() {
  const { t } = useI18n();
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactValues>({
    resolver: zodResolver(schema),
    defaultValues: { subject: 'BOOKING', name: '', email: '', message: '' },
  });

  const onSubmit = handleSubmit(() => setSent(true));

  if (sent) {
    return (
      <div className={styles.empty}>
        <p className="micro">◦ TRANSMISSION RECEIVED</p>
        <p className="fg-dim">We will get back within 72 hours.</p>
        <button
          type="button"
          className="btn btn-ghost"
          style={{ marginTop: 16 }}
          onClick={() => {
            reset();
            setSent(false);
          }}
        >
          NEW MESSAGE
        </button>
      </div>
    );
  }

  return (
    <>
      <h3>OPEN A CHANNEL</h3>
      <p className={styles.intro}>
        For artist booking, label proposals, partnerships, press credentials, or general
        inquiries.
      </p>
      <form className={styles.form} onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="c-subject">{t('label.subject')}</label>
          <select id="c-subject" {...register('subject')}>
            {SUBJECTS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="c-name">{t('label.name')}</label>
          <input id="c-name" placeholder="FIRST LAST / AGENCY" {...register('name')} />
          {errors.name && <span className={styles.err}>{errors.name.message}</span>}
        </div>
        <div className="field">
          <label htmlFor="c-email">{t('label.email')}</label>
          <input
            id="c-email"
            type="email"
            placeholder="YOU@DOMAIN.COM"
            {...register('email')}
          />
          {errors.email && <span className={styles.err}>{errors.email.message}</span>}
        </div>
        <div className="field">
          <label htmlFor="c-msg">{t('label.message')}</label>
          <textarea id="c-msg" placeholder="..." {...register('message')} />
          {errors.message && <span className={styles.err}>{errors.message.message}</span>}
        </div>
        <button
          type="submit"
          className="btn btn-fill btn-arrow"
          style={{ alignSelf: 'flex-start' }}
        >
          TRANSMIT
        </button>
      </form>
    </>
  );
}
